<script setup>
import { computed, ref, watch, onMounted } from "vue";
import { useStore } from "vuex";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import Spinner from "components/jiffy/vue-cart/Spinner.vue";
import { validateFile } from "common/utils/file";
import { trackRudderstackImagePlacementSelection } from "common/utils/rudderstack/tracker";
import {
  trackUploadInitiated,
  trackUploadSuccess,
  trackRemoveImage,
  trackCustomPlacementInteraction
} from "../utils/tracker";
import { MODES, NECK_LINE_MODES, SURFACES } from "../misc/constants";
import ArtworkSize from "./ArtworkSize.vue";
import ArtworkCanvas from "./ArtworkCanvas.vue";
import ArtworkDesign from "./ArtworkDesign.vue";
import ArtworkSurfacePosition from "./ArtworkSurfacePosition.vue";
import ArtworkMeasurement from "./ArtworkMeasurement.vue";
import ArtworkUpload from "./ArtworkUpload.vue";
import ArtworkError from "./ArtworkError.vue";
import ArtworkNeckLines from "./ArtworkNeckLines/ArtworkNeckLines.vue";
import ArtworkNeckLineCustomization from "./ArtworkNeckLineCustomization.vue";
import ArtworkLegend from "./ArtworkLegend.vue";
import { getDisplayedSize, getSizeWithSteps } from "../utils/sizes";
import ArtworkNecklinesWarning from "./ArtworkNecklinesWarning.vue";
import ArtworkPriceNotification from "./ArtworkPriceNotification.vue";

const props = defineProps({
  sideKey: {
    type: String,
    required: true
  },
  titlePrefix: {
    type: String,
    required: true
  },
  availablePlacements: {
    type: Array,
    required: true
  },
  isPreview: {
    type: Boolean,
    default: false
  }
});

const store = useStore();
const config = computed(() => store.getters.config);
const experiments = computed(() => store.getters.experiments);
const isRestoring = computed(() => store.getters["app/isRestoring"]);
const legendOnceShown = ref(true);
const legendShown = ref(false);
const artworkReference = ref(null);
const supportsMade = computed(() => store.getters["app/supportsMade"]);
const isMadeEnabled = computed(() => store.getters["app/mode"] === MODES.MADE);
const neckLineMode = computed(() => store.getters[`surface_${props.sideKey}/neckLineMode`]);
const neckLineLabel = computed(() => store.getters[`surface_${props.sideKey}/neckLineLabel`]);
const surfaceLabel = computed(() => store.getters[`surface_${props.sideKey}/surfaceLabel`]);
const neckLines = computed(() => store.getters["product/neckLines"][surfaceLabel.value]);
const getNeckLine = label => store.getters["product/getNeckLine"](surfaceLabel.value, label);
const filteredNeckLines = computed(() => {
  const filledSizes = store.getters["app/filledSizes"];
  const lines = neckLines.value || {};
  return Object.keys(lines).reduce((acc, key) => {
    if (filledSizes.includes(key)) {
      acc[key] = lines[key];
    }
    return acc;
  }, {});
});
const disabledCustomization = computed(() => !Object.keys(filteredNeckLines.value).length);
const shownCustomNeckLines = computed(
  () => neckLines.value && neckLineMode.value === NECK_LINE_MODES.CUSTOM
);
const uploadErrorText = computed(() => store.getters[`artwork_${props.sideKey}/uploadErrorText`]);
const artworkExists = computed(() => store.getters[`artwork_${props.sideKey}/artworkExists`]);
const artworkSize = computed(() => store.getters[`artwork_${props.sideKey}/artworkSize`]);
const currentArtwork = computed(() => store.getters[`artwork_${props.sideKey}/artwork`]);
let cachedSize = store.getters[`artwork_${props.sideKey}/artworkSize`];
/* The `nonZeroArtworkSize` computed property is used to prevent showing the artwork size is 0x0 in inputs. */
const nonZeroArtworkSize = computed(() => {
  const currentSize = store.getters[`artwork_${props.sideKey}/artworkSize`];
  if (!currentSize.width && !currentSize.height) return cachedSize;
  cachedSize = currentSize;
  return currentSize;
});
const artworkUrl = computed(() => store.getters[`artwork_${props.sideKey}/artworkUrl`]);
const surface = computed(() => store.getters[`surface_${props.sideKey}/surface`]);
const downscaleFactor = computed(() => store.getters[`surface_${props.sideKey}/downscaleFactor`]);
const shownPriceNotification = computed(
  () =>
    !config.value.made_sample_pdp &&
    !artworkExists.value &&
    config.value.second_placement_price &&
    surfaceLabel.value !== SURFACES.FRONT &&
    isMadeEnabled.value
);
const setDownscaleFactor = value =>
  store.commit(`surface_${props.sideKey}/setDownscaleFactor`, value);
const oneInch = computed(() => store.getters[`surface_${props.sideKey}/oneInch`]);
const pxToInch = size => store.getters[`surface_${props.sideKey}/convertPxToInches`](size);
const inchToPx = size => store.getters[`surface_${props.sideKey}/convertInchesToPx`](size);
const getSurface = label => store.getters[`surface_${props.sideKey}/getSurface`](label);
const title = computed(() => `${props.titlePrefix} Image Placement`);
const customNeckLines = computed(() => store.getters[`surface_${props.sideKey}/customNeckLines`]);
const maxLineHeight = max => getSizeWithSteps(max - pxToInch(artworkSize.value.height));
const currentNeckLineIn = computed(() => {
  const label = neckLineLabel.value;
  const { default: defaultLineValue, max_height: max } = getNeckLine(label);
  const customLineValue = customNeckLines.value[label] ?? defaultLineValue;
  const minValue = shownCustomNeckLines.value
    ? Math.min(customLineValue, maxLineHeight(max))
    : defaultLineValue;
  return getDisplayedSize(minValue);
});
const currentNeckLinePx = computed(() => inchToPx(currentNeckLineIn.value));
const maxCanvasHeight = computed(() => inchToPx(getNeckLine(neckLineLabel.value).max_height));
const rudderstackTrackProperties = computed(() => ({
  placement: surfaceLabel.value,
  artwork_id: currentArtwork.value.id,
  design_dimensions: [
    {
      width: pxToInch(artworkSize.value.width),
      height: pxToInch(artworkSize.value.height)
    }
  ]
}));
const presetLoaded = computed(() => store.getters[`artwork_${props.sideKey}/presetLoaded`]);

const hideLegend = () => {
  if (artworkExists.value && legendShown.value) {
    legendShown.value = false;
    legendOnceShown.value = false;
  }
};

const toggleLegend = () => {
  if (
    neckLineMode.value === NECK_LINE_MODES.CUSTOM &&
    legendOnceShown.value &&
    surfaceLabel.value !== SURFACES.FRONT_LEFT_CHEST
  ) {
    legendShown.value = true;
  } else {
    hideLegend();
  }
};

const updateSurfaceLabel = label => {
  store.dispatch(`surface_${props.sideKey}/setSurfaceLabel`, label);
  toggleLegend();
  trackRudderstackImagePlacementSelection("Selected", rudderstackTrackProperties.value);
};

const updateNeckLineLabel = label => {
  store.commit(`surface_${props.sideKey}/setNeckLineLabel`, label);
};

const updateNeckLineHeight = ({ label, height }) => {
  const { max_height: max } = getNeckLine(label);
  store.dispatch(`surface_${props.sideKey}/setNeckLineHeight`, {
    neckLineLabel: label,
    height: Math.max(Math.min(height, maxLineHeight(max)), 0)
  });
};

const updateNeckLineHeightFromCanvas = height => {
  updateNeckLineHeight({ label: neckLineLabel.value, height: pxToInch(height) });
};

const endNeckLineHeightFromCanvas = height => {
  const roundedHeight = getSizeWithSteps(pxToInch(height));
  updateNeckLineHeight({ label: neckLineLabel.value, height: roundedHeight });
};

const updateNeckLineCustomization = mode => {
  store.commit(`surface_${props.sideKey}/setNeckLineMode`, mode);
  toggleLegend();
};

const clearCustomNeckLines = () => {
  store.dispatch(`surface_${props.sideKey}/clearNeckLines`);
};

const removeArtwork = () => {
  const { filetype, filesize, filename } = currentArtwork.value;
  const storedRudderstackTrackProperties = rudderstackTrackProperties.value;

  // eslint-disable-next-line no-alert
  if (window.confirm("Do you really want to delete the artwork?")) {
    hideLegend();
    store.dispatch(`artwork_${props.sideKey}/removeArtwork`);
    clearCustomNeckLines();
    if (surfaceLabel.value === SURFACES.FRONT_LEFT_CHEST) {
      updateSurfaceLabel(SURFACES.FRONT);
    }
    trackRemoveImage({ location: props.sideKey, filetype, filesize, filename });
    trackRudderstackImagePlacementSelection("Removed", storedRudderstackTrackProperties);
  }
};

const endResize = size => {
  const isIncreasedHeight = size.height > artworkSize.value.height;
  store.dispatch(`artwork_${props.sideKey}/setArtworkSize`, size);
  if (isIncreasedHeight) {
    updateNeckLineHeight({ label: neckLineLabel.value, height: currentNeckLineIn.value });
  }

  trackRudderstackImagePlacementSelection("Size Edited", rudderstackTrackProperties.value);
};

let previousNeckLineLabel = neckLineLabel.value;
let previousNeckLineHeight = currentNeckLineIn.value;
let previousArtworkWidth = pxToInch(artworkSize.value.width);
let previousArtworkHeight = pxToInch(artworkSize.value.height);

const initPreviousValues = () => {
  previousNeckLineLabel = neckLineLabel.value;
  previousNeckLineHeight = currentNeckLineIn.value;
  previousArtworkWidth = pxToInch(artworkSize.value.width);
  previousArtworkHeight = pxToInch(artworkSize.value.height);
};

const initArtworkSize = ({ width, height }) => {
  const { max_height: max, default: defaultNeckLine } = getNeckLine(neckLineLabel.value);
  const newHeight = Math.min(height, inchToPx(max - defaultNeckLine));
  const newWidth = (width / height) * newHeight;
  const newSize = { width: newWidth, height: newHeight };
  store.dispatch(`artwork_${props.sideKey}/setArtworkSize`, newSize);
  initPreviousValues();
};

const resetNeckLineWithArtwork = ({ label, height }) => {
  const { max_height: max } = getNeckLine(label);
  const { width: aWidth, height: aHeight } = artworkSize.value;
  const newHeight = Math.min(inchToPx(max - height), aHeight);
  const newWidth = (aWidth / aHeight) * newHeight;
  endResize({ width: newWidth, height: newHeight });
  updateNeckLineHeight({ label, height });
};

const clearArtworkSize = () => {
  store.commit(`artwork_${props.sideKey}/clearArtworkSize`);
};

const getInteractionType = () => {
  if (
    neckLineLabel.value !== previousNeckLineLabel ||
    currentNeckLineIn.value !== previousNeckLineHeight
  ) {
    return "moved";
  }
  if (
    pxToInch(artworkSize.value.width) !== previousArtworkWidth ||
    pxToInch(artworkSize.value.height) !== previousArtworkHeight
  ) {
    return "resized";
  }
  return "no_change";
};

const trackCustomPlacement = (changeType = "drag") => {
  let interactionType = changeType;
  if (interactionType === "drag") {
    interactionType = getInteractionType();
  }

  if (
    currentArtwork.value === null ||
    neckLineLabel.value === "default" ||
    interactionType === "no_change"
  ) {
    return;
  }

  const payload = {
    interaction_type: interactionType,
    placement: surfaceLabel.value,
    artwork_id: currentArtwork.value.id,
    placement_details: {
      neck_line: {
        label: neckLineLabel.value,
        height: currentNeckLineIn.value
      },
      design_dimensions: {
        width: pxToInch(artworkSize.value.width),
        height: pxToInch(artworkSize.value.height)
      }
    }
  };

  trackCustomPlacementInteraction(payload);
  initPreviousValues();
};

const uploadArtwork = file => {
  if (!supportsMade.value) {
    window.alert("This product color is not supported in Jiffy Custom Shirts.");
    return;
  }

  const extensions = ["png", "gif", "jpeg", "jpg", "svg", "svg+xml", "heic", "heif"];
  const accept = extensions.map(extension => `image/${extension}`);
  const extension = `(${extensions.join("|")})`;

  if (validateFile(file, { extension, accept })) {
    trackUploadInitiated(props.sideKey, store.getters["app/mode"], file);
    store.commit("app/setMode", MODES.MADE);
    store.dispatch(`artwork_${props.sideKey}/upload`, { originalFile: file });

    if (artworkReference.value) {
      setTimeout(() => {
        artworkReference.value.scrollIntoView({ behavior: "smooth" });
      }, 500);
    }
  }
};

watch(artworkExists, artwork => {
  artwork?.url && trackUploadSuccess(props.sideKey, artwork.file, artwork);
});

onMounted(() => {
  initPreviousValues();
});
</script>

<template>
  <div
    ref="artworkReference"
    class="artwork"
    :class="{ 'artwork--disabled': !supportsMade }"
    data-testid="artwork"
  >
    <div class="artwork__title-wrapper">
      <div class="artwork__title" data-testid="artwork-title">{{ title }}</div>
      <button
        v-if="artworkExists"
        class="artwork__remove-artwork"
        type="button"
        aria-label="Remove artwork"
        data-testid="remove-artwork"
        @click="removeArtwork"
      >
        <UseSvg icon="trash" type="print" />
        <span class="artwork__delete-text">Delete</span>
      </button>
      <ArtworkPriceNotification
        v-else-if="shownPriceNotification"
        :price="config.second_placement_price"
        class="artwork__title-price"
      />
    </div>

    <div v-if="uploadErrorText" class="artwork__error" data-testid="artwork-error">
      <ArtworkError :message="uploadErrorText" />
    </div>

    <div class="artwork__shadow-wrapper">
      <div v-if="isRestoring" class="artwork__loader">
        <Spinner class="artwork__spinner" />
      </div>
      <div
        class="artwork__canvas"
        :class="{ 'artwork__canvas--preview': props.isPreview }"
        data-testid="artwork-canvas"
        @click="hideLegend"
      >
        <ArtworkCanvas
          :key="surface.picture"
          :artwork-url="artworkUrl"
          :surface="surface"
          :max-boundings-height="maxCanvasHeight"
          :surface-label="surfaceLabel"
          :get-surface="getSurface"
          :downscale-factor="downscaleFactor"
          :current-neck-line-px="currentNeckLinePx"
          :shown-canvas-border="artworkExists"
          :shown-custom-neck-lines="shownCustomNeckLines"
          @update-downscale-factor="setDownscaleFactor"
          @update-neck-line-height="updateNeckLineHeightFromCanvas"
          @end-neck-line-height="endNeckLineHeightFromCanvas"
          @track-custom-placement="trackCustomPlacement"
          @init-previous-values="initPreviousValues"
          @mouseup="trackCustomPlacement('drag')"
        >
          <template #measurement>
            <ArtworkMeasurement
              v-if="artworkExists"
              :get-surface="getSurface"
              :surface-label="surfaceLabel"
              :downscale-factor="downscaleFactor"
              :shown-custom-neck-lines="shownCustomNeckLines"
              :neck-line-label="neckLineLabel"
              :current-neck-line-in="currentNeckLineIn"
              :current-neck-line-px="currentNeckLinePx"
            />
          </template>

          <template #default>
            <template v-if="artworkExists">
              <ArtworkDesign
                :surface-label="surfaceLabel"
                :surface="surface"
                :one-inch="oneInch"
                :artwork-url="artworkUrl"
                :artwork-size="artworkSize"
                @resize="endResize"
                @init-size="initArtworkSize"
                @clear-artwork-size="clearArtworkSize"
              />
              <ArtworkLegend v-if="legendShown" :disabled="disabledCustomization" />
            </template>
            <ArtworkUpload v-else :side-key="sideKey" @upload-artwork="uploadArtwork" />
          </template>
        </ArtworkCanvas>
      </div>

      <div
        class="artwork__controls-bg-wrapper"
        :class="{
          'artwork__controls-bg-wrapper--bg': config.made_custom_neck_distances
        }"
      >
        <div class="artwork__controls" :class="{ 'artwork__controls--disabled': !artworkExists }">
          <div class="artwork__controls-title artwork__controls-title--bold">Select Placement</div>
          <ArtworkSurfacePosition
            :available-placements="props.availablePlacements"
            :surface-label="surfaceLabel"
            @update-surface-label="updateSurfaceLabel"
          />
        </div>

        <div
          v-if="config.made_custom_neck_distances"
          class="artwork__controls"
          :class="{ 'artwork__controls--disabled': !artworkExists }"
          data-testid="neckline-controls"
        >
          <div class="artwork__controls-title">Distance from neck line</div>
          <ArtworkNeckLineCustomization
            :neck-line-mode="neckLineMode"
            :disabled-customization="disabledCustomization"
            @update-neck-line-customization="updateNeckLineCustomization"
          />
          <ArtworkNeckLines
            v-if="shownCustomNeckLines"
            :neck-lines="filteredNeckLines"
            :custom-neck-lines="customNeckLines"
            :max-distance="maxLineHeight"
            :neck-line-label="neckLineLabel"
            @update-neck-line-label="updateNeckLineLabel"
            @update-neck-line-height="updateNeckLineHeight"
            @clear-custom-neck-lines="clearCustomNeckLines"
            @reset-neck-line="resetNeckLineWithArtwork"
            @init-previous-values="initPreviousValues"
            @track-custom-placement="trackCustomPlacement"
          />
          <ArtworkNecklinesWarning v-if="disabledCustomization" />
        </div>
      </div>

      <div class="artwork__controls" :class="{ 'artwork__controls--disabled': !artworkExists }">
        <div class="artwork__controls-title artwork__controls-title--bold">Image Size</div>
        <ArtworkSize
          :artwork-size="nonZeroArtworkSize"
          :surface="surface"
          :downscale-factor="downscaleFactor"
          :inch-to-px="inchToPx"
          :px-to-inch="pxToInch"
          @update-size="endResize"
          @track-custom-placement="trackCustomPlacement"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/pod/settings";
@import "frontend/init/jiffy/mixin";
@import "frontend/init/jiffy/button/mixin";

.artwork {
  font-family: $pod-font-family;

  &--disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  @include with-container(container, $pod-pdp-desktop-response) {
    min-height: 100%;
    grid-template-columns: 100%;
    display: grid;
    grid-template-rows: min-content 1fr;
  }

  &__title-wrapper {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    position: relative;
    height: 32px;
  }

  &__title-price {
    position: absolute;
    top: -10px;
    right: 0;
  }

  &__error {
    margin-bottom: 10px;
  }

  &__title {
    font-size: 16px;
    font-weight: 500;
    color: $black;
  }

  &__delete-text {
    font-size: 16px;
    font-weight: 500;
    color: $black;
    padding-right: 5px;
  }

  &__remove-artwork {
    @include clear-button;
    @include svg-wrapper;

    padding: 4px;
    width: 100px;
    height: 32px;
    border: 1px solid $pod-gray-modern-200;
    border-radius: 6px;
    background: $pod-danger-light;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__canvas {
    margin: 0 (-$mobile-spacing);

    @include with-tablet {
      margin: 0;
    }

    @include with-container(container, $pod-pdp-desktop-response) {
      border-radius: 4px;
      overflow: hidden;
    }

    &--preview {
      @include with-container(container, $pod-pdp-desktop-response) {
        max-height: 450px;
      }
    }
  }

  &__controls-bg-wrapper {
    margin: 14px 0 11px;

    &--bg {
      border-radius: 4px;
      background-color: $pod-gray-modern-100;
      padding: 12px;
    }
  }

  &__controls {
    &--disabled {
      pointer-events: none;
      opacity: 0.5;
    }

    & + & {
      margin-top: 14px;
    }
  }

  &__controls-title {
    margin-bottom: 4px;

    &--bold {
      font-weight: bold;
    }
  }

  &__shadow-wrapper {
    @include with-container(container, $pod-pdp-desktop-response) {
      padding: 8px;
      border-radius: 8px;
      border: 1px solid $pod-gray-modern-200;
      background-color: $white;
      box-shadow: $pod-card-shadow;
      position: relative;
    }
  }

  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba($black, 0.6);
  }

  &__spinner {
    width: 30px;
  }
}
</style>
